import React, { useEffect, useState } from 'react';

interface BreedPhotoProps {
  /** Breed key from formData.raza (e.g. "labrador_retriever"). */
  breedKey: string | null | undefined;
  /** Tailwind size classes — defaults to a 64px round avatar. */
  className?: string;
  /** alt text override. Defaults to the dog's name. */
  alt?: string;
}

/**
 * Round breed avatar shown across personalized funnel steps.
 *
 * Resolves to `/images/breeds/<breedKey>.webp` if the user picked a
 * breed in P3. If WebP fails, tries the PNG asset before falling back
 * to the Milo y Lola placeholder when:
 *  - `breedKey` is empty / null / "mestizo" / "no_lo_se"
 *  - the specific breed image 404s (mestizo cross with breed_code that
 *    isn't in our 69-image set)
 *
 * The fallback path stays in sync with `P1_Welcome.tsx` and
 * `P9_Reveal.tsx`, so visitors who never picked a breed see the same
 * dog illustration they saw on the welcome screen.
 */
const FALLBACK_SRC = '/images/funnel/dog-illustration.png';
const UNKNOWN_BREED_TOKENS = new Set(['', 'mestizo', 'cruce', 'mestizo_cruce', 'no_lo_se', 'desconocido', 'unknown']);

const isKnownBreed = (key: string | null | undefined): key is string => {
  if (!key) return false;
  return !UNKNOWN_BREED_TOKENS.has(key.toLowerCase().trim());
};

export const BreedPhoto: React.FC<BreedPhotoProps> = ({
  breedKey,
  className = 'w-16 h-16',
  alt = 'Foto del perro',
}) => {
  const knownBreed = isKnownBreed(breedKey);
  const webpSrc = knownBreed ? `/images/breeds/${breedKey}.webp` : FALLBACK_SRC;
  const pngSrc = knownBreed ? `/images/breeds/${breedKey}.png` : FALLBACK_SRC;
  const [src, setSrc] = useState(webpSrc);

  useEffect(() => {
    setSrc(webpSrc);
  }, [webpSrc]);

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} object-cover rounded-full bg-[#FAEDE1]`}
      onError={() => {
        if (src === webpSrc && pngSrc !== webpSrc) {
          setSrc(pngSrc);
        } else if (src !== FALLBACK_SRC) {
          setSrc(FALLBACK_SRC);
        }
      }}
    />
  );
};
